import { Authorization } from '@/constants/authorization';
import { ResponseType } from '@/interfaces/database/base';
import i18n from '@/locales/config';
import authorizationUtil, {
  getAuthorization,
  redirectToLogin,
} from '@/utils/authorization-util';
import { message, notification } from 'antd';
import { RequestMethod, extend } from 'umi-request';
import { getApiErrorNotification, shouldClearUserSession } from './api-error';
import { convertTheKeysOfTheObjectToSnake } from './common-util';

const FAILED_TO_FETCH = 'Failed to fetch';

export const RetcodeMessage = {
  200: i18n.t('message.200'),
  201: i18n.t('message.201'),
  202: i18n.t('message.202'),
  204: i18n.t('message.204'),
  400: i18n.t('message.400'),
  401: i18n.t('message.401'),
  403: i18n.t('message.403'),
  404: i18n.t('message.404'),
  406: i18n.t('message.406'),
  410: i18n.t('message.410'),
  413: i18n.t('message.413'),
  422: i18n.t('message.422'),
  500: i18n.t('message.500'),
  502: i18n.t('message.502'),
  503: i18n.t('message.503'),
  504: i18n.t('message.504'),
};
export type ResultCode =
  | 200
  | 201
  | 202
  | 204
  | 400
  | 401
  | 403
  | 404
  | 406
  | 410
  | 413
  | 422
  | 500
  | 502
  | 503
  | 504;

const errorHandler = (error: {
  response: Response;
  message: string;
}): Response => {
  const { response } = error;
  if (error.message === FAILED_TO_FETCH) {
    notification.error({
      description: i18n.t('message.networkAnomalyDescription'),
      message: i18n.t('message.networkAnomaly'),
    });
  } else {
    if (response && response.status) {
      // umi-request passes HTTP failures through the response interceptor after
      // this handler returns.  Let that single path display the backend reason;
      // notifying here as well produced duplicate 403/409 messages.
      return response;
    }
  }
  return response ?? { data: { code: 1999 } };
};

const request: RequestMethod = extend({
  errorHandler,
  timeout: 300000,
  getResponse: true,
});

request.interceptors.request.use((url: string, options: any) => {
  const data = convertTheKeysOfTheObjectToSnake(options.data);
  const params = convertTheKeysOfTheObjectToSnake(options.params);

  return {
    url,
    options: {
      ...options,
      data,
      params,
      headers: {
        ...(options.skipToken
          ? undefined
          : { [Authorization]: getAuthorization() }),
        ...options.headers,
      },
      interceptors: true,
    },
  };
});

request.interceptors.response.use(async (response: Response, options) => {
  if (options.responseType === 'blob' && response.status !== 401) {
    return response;
  }

  let data: ResponseType;
  try {
    data = await response?.clone()?.json();
  } catch {
    data = {
      code: response?.status || 500,
      data: undefined,
      message: RetcodeMessage[response?.status as ResultCode] || '',
      status: response?.status || 500,
    };
  }
  if (data?.code === 100) {
    message.error(data?.message);
  } else if (shouldClearUserSession(data, response.status)) {
    const sessionError = {
      ...data,
      code: 401,
      reason_code: data.reason_code || 'UNAUTHENTICATED',
    };
    notification.error({
      ...getApiErrorNotification(sessionError, i18n.t('message.hint')),
      duration: 3,
    });
    authorizationUtil.removeAll();
    redirectToLogin();
  } else if (options.suppressNotification) {
    return response;
  } else if (data?.code !== 0) {
    notification.error({
      ...getApiErrorNotification(data, i18n.t('message.hint')),
      duration: 3,
    });
  }
  return response;
});

export default request;

export const get = (url: string) => {
  return request.get(url);
};

export const post = (url: string, body: any) => {
  return request.post(url, { data: body });
};

export const drop = () => {};

export const put = () => {};
